'use client';

import { useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { Icon, type IconName } from '@/components/ui/icon';
import { Avatar } from '@/components/ui/avatar';
import { StatusPill, ModePill } from '@/components/ui/pills';
import { fcfa } from '@/lib/format';
import { cn } from '@/lib/utils';

export type Step = { icon: IconName; title: string; text: string };

const STEP_DURATION_MS = 4000;

/**
 * Auto-advancing tutorial carousel for "Comment ça marche" — this is what
 * the Hero's "Voir comment ça marche" CTA actually scrolls to (see
 * routes.ts), so it needs to read as a real walkthrough, not four static
 * cards.
 *
 * Deliberately hand-rolled: 4 fixed steps don't justify a carousel
 * dependency (Embla/Swiper) or an animation library (Framer Motion) —
 * everything here is CSS transitions/keyframes + plain React state, so the
 * bundle cost of "ultra moderne" motion stays close to zero.
 */
export function StepCarousel({ steps }: { steps: Step[] }) {
  const [active, setActive] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef<number | null>(null);

  // WCAG 2.2.2 (Pause, Stop, Hide): auto-updating content past 5s needs a
  // user-operable pause that STICKS — hoverPaused alone would silently
  // un-pause the moment the pointer leaves, which defeats the point.
  const paused = userPaused || hoverPaused;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (paused || reducedMotion) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % steps.length);
    }, STEP_DURATION_MS);
    return () => clearInterval(id);
  }, [paused, reducedMotion, steps.length]);

  const goTo = (i: number) => setActive(((i % steps.length) + steps.length) % steps.length);

  const onTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
    setHoverPaused(true);
  };
  const onTouchEnd = (e: ReactTouchEvent<HTMLDivElement>) => {
    const startX = touchStartX.current;
    setHoverPaused(false);
    if (startX === null) return;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const dx = endX - startX;
    if (Math.abs(dx) > 40) goTo(active + (dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const step = steps[active];
  if (!step) return null;

  return (
    <div
      className="rounded-card border border-line bg-white p-6 nav:p-8"
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocus={() => setHoverPaused(true)}
      onBlur={() => setHoverPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="group"
      aria-roledescription="carrousel"
      aria-label="Comment ça marche, étape par étape"
    >
      <div className="mb-7 flex gap-2">
        {steps.map((s, i) => (
          <div key={s.title} className="h-[3px] flex-1 overflow-hidden rounded-full bg-line">
            <div
              key={i === active ? `active-${active}` : 'idle'}
              className={cn(
                'h-full rounded-full bg-green',
                i < active && 'w-full',
                i > active && 'w-0',
                i === active && (reducedMotion ? 'w-full' : 'animate-tmfill'),
              )}
              style={
                i === active && !reducedMotion
                  ? {
                      animationDuration: `${STEP_DURATION_MS}ms`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }
                  : undefined
              }
            />
          </div>
        ))}
      </div>

      <div className="grid items-center gap-10 nav:grid-cols-[1fr_1.1fr]">
        <div>
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-[12px] bg-green-bg text-green">
            <Icon name={step.icon} size={22} />
          </div>
          <div className="mb-1.5 font-num text-[13px] font-bold text-gold-d">
            {String(active + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
          </div>
          <h3 className="m-0 mb-2.5 font-display text-[22px] font-bold text-navy">{step.title}</h3>
          <p className="m-0 text-[15px] leading-[1.55] text-[#4b5567]">{step.text}</p>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex gap-2">
              {steps.map((s, i) => (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Étape ${i + 1} : ${s.title}`}
                  aria-current={i === active ? 'step' : undefined}
                  className={cn(
                    'h-2 w-2 rounded-full transition-[transform,background-color] duration-150 hover:scale-125',
                    i === active ? 'bg-green' : 'bg-line hover:bg-mut',
                  )}
                />
              ))}
            </div>
            {!reducedMotion && (
              <button
                type="button"
                onClick={() => setUserPaused((p) => !p)}
                aria-pressed={userPaused}
                aria-label={
                  userPaused
                    ? 'Reprendre la lecture automatique'
                    : 'Mettre en pause la lecture automatique'
                }
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-green/50 hover:bg-green-bg hover:text-green"
              >
                {userPaused ? (
                  <svg
                    width="10"
                    height="12"
                    viewBox="0 0 10 12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M0 0l10 6-10 6z" />
                  </svg>
                ) : (
                  <svg
                    width="10"
                    height="12"
                    viewBox="0 0 10 12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <rect width="3" height="12" />
                    <rect x="7" width="3" height="12" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="rounded-[20px] bg-cream p-5">
          <StepIllustration step={active} />
        </div>
      </div>
    </div>
  );
}

/**
 * One mini-mockup per step, reusing the Hero phone's exact visual language
 * (Avatar/StatusPill/ModePill, same colors) instead of inventing a new
 * illustration style. No images: everything here is markup + the existing
 * SVG icon set, so this carries zero extra network requests and zero CLS.
 */
function StepIllustration({ step }: { step: number }) {
  if (step === 0) {
    const rows = [
      { label: 'Cotisation', value: `${fcfa(25000)}/semaine` },
      { label: 'Jour de ramassage', value: 'Samedi' },
      { label: 'Pénalité de retard', value: `${fcfa(500)}/jour` },
    ];
    return (
      <div className="space-y-2.5">
        <div className="text-[11px] font-semibold tracking-[.4px] text-mut uppercase">Réglages</div>
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between rounded-[12px] bg-white px-3.5 py-3"
          >
            <div>
              <div className="text-[13px] font-semibold text-ink">{r.label}</div>
              <div className="font-num text-[12px] text-mut">{r.value}</div>
            </div>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-bg text-green">
              <Icon name="check" size={13} />
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (step === 1) {
    const members = [
      { name: 'Awa Koné', color: 'br' as const },
      { name: 'Mariam Traoré', color: 'gd' as const },
      { name: 'Fatou Diallo', color: 'pl' as const },
    ];
    return (
      <div className="space-y-2">
        {members.map((m) => (
          <div
            key={m.name}
            className="flex items-center gap-2.5 rounded-[12px] bg-white px-3.5 py-2.5"
          >
            <Avatar name={m.name} size={30} color={m.color} />
            <span className="text-[13.5px] font-semibold text-ink">{m.name}</span>
          </div>
        ))}
        <div className="flex items-center justify-center gap-1.5 rounded-[12px] border border-dashed border-line py-2.5 text-[13px] font-semibold text-green">
          <Icon name="plus" size={14} /> Ajouter un membre
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="flex flex-col items-center rounded-[12px] bg-white px-5 py-7 text-center">
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-bg text-green">
          <Icon name="check" size={24} />
        </span>
        <div className="font-num text-[26px] font-bold text-navy">{fcfa(25000)}</div>
        <div className="mt-1 text-[13px] text-mut">Cotisation reçue</div>
        <ModePill mode="wave" className="mt-3" />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-[12px] bg-white px-4 py-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-bg text-gold-d">
        <Icon name="bell" size={17} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13.5px] font-semibold text-ink">Adjoua Kouassi</span>
          <StatusPill status="retard" />
        </div>
        <div className="mt-1 text-[12.5px] text-mut">
          Rappel envoyé automatiquement — 3 jours de retard
        </div>
      </div>
    </div>
  );
}
